import {StoreModel} from './utils'
import {CommerceAPI} from 'commerce-api'
import {Status, StatusMap} from '../types/commerce'
import {RootStore} from './RootStore'
import {computed, flow, flowResult, makeObservable, observable} from 'mobx'
import {
  FindStoresResponse,
  StoreDetailResponseItem,
} from '../types/store/storeDetails'
import {GoogleMapsGeocoder} from '../services/googleMapsGeocoder'
 
export class StoreDetailsStore implements StoreModel {
  api: CommerceAPI
  currentSearch: StoreDetailResponseItem[]
  status: Status
  geocoder: GoogleMapsGeocoder
  cachedLocationSearches: Map<string, {timestamp: number, result: {lat: number, lng: number} | null}>

  constructor(rootStore: RootStore, initialState = {currentSearch: [], status: StatusMap.IDLE}) {
    this.currentSearch = initialState?.currentSearch
    this.status = initialState?.status

    makeObservable(this, {
      status: observable,
      loading: computed,
      findStoresByCoords: flow.bound,
      findNearestStoreByCoords: flow.bound,
      findStore: flow.bound,
      currentSearch: observable,
      getAddressCoordinates: flow.bound
    })

    this.api = rootStore.api
    this.geocoder = new GoogleMapsGeocoder({API_KEY: rootStore?.globalStore?.googleMapsAPIkey})
    this.cachedLocationSearches = new Map<string, {timestamp: number, result: {lat: number, lng: number} | null}>()
  }

  get loading() {
    return this.status === StatusMap.PENDING
  }

  *findStoresByCoords(coords: {lat: number; lng: number}) {
    try {
      this.status = StatusMap.PENDING
      const response: FindStoresResponse = yield this.api.shopperStores.getStores({
        parameters: {
          latitude: coords.lat,
          longitude: coords.lng,
          distanceUnit: 'mi',
          maxDistance: 50,
        },
      })
      if (!response?.data || !response.data.length) {
        this.currentSearch = []
        return
      }

      this.currentSearch = response.data
        .filter(
          (store) =>
            !Object.hasOwn(store, 'c_displayOnStoreFinder') || store.c_displayOnStoreFinder,
        )
        .slice(0, 5)

      this.status = StatusMap.DONE
    } catch (e) {
      this.status = StatusMap.ERROR
      console.error(`Failed to find stores with coors: ${coords.lng} ${coords.lat}`, e)
    }
  }

  *findStore(storeId: string) {
    try {
      this.status = StatusMap.PENDING
      const response: StoreDetailResponseItem = yield this.api.shopperStores.getStore({
        parameters: {
          storeId,
        },
      })
      this.status = StatusMap.DONE

      return response
    } catch (e) {
      this.status = StatusMap.ERROR
      console.error(`Failed to get store details by storeID: ${storeId}`, e)
    }
  }

  *findNearestStoreByCoords(coords: {lat: number; lng: number}) {
    try {
      this.status = StatusMap.PENDING
      const response: FindStoresResponse = yield this.api.shopperStores.getStores({
        parameters: {
          latitude: coords.lat,
          longitude: coords.lng,
          distanceUnit: 'mi',
          maxDistance: 50,
        },
      })

      if (!response?.data || !response.data.length) {
        return null
      }

      this.status = StatusMap.DONE

      return response.data.filter(
          (store) =>
            !Object.hasOwn(store, 'c_displayOnStoreFinder') || store.c_displayOnStoreFinder,
        )[0]
    } catch (e) {
      this.status = StatusMap.ERROR
      console.error(`Failed to find stores with coors: ${coords.lng} ${coords.lat}`, e)
      return null
    }
  }

  get asJson() {
    return {
      currentSearch: this.currentSearch,
    }
  }

  /**
   * Lookup co-ordinates from a postcode
   * @param deliveryPostalCode string postcode to lookup
   */
  *getAddressCoordinates(deliveryPostalCode: string) { 

    if(!deliveryPostalCode) return null
    
    // Attempt to retrieve local cached copy of lookup
    const cachedResponse = this.cachedLocationSearches.get(deliveryPostalCode)

    if(cachedResponse) { 
        
        const TTL = 60000 // 1 minute cache value - enough to protect against React sillyness. 
        const cacheAge = Date.now() - cachedResponse.timestamp

        if(cacheAge > TTL) { 
            this.cachedLocationSearches.delete(deliveryPostalCode)
        } else { 
            console.debug(`getAddressCoordinates returned cached result - cached age ${cacheAge}ms`)
            return cachedResponse.result
        }
    }
    
    // Retrieve result from the geocode 
    const result: {lat: number, lng: number} | null = yield flowResult(this.geocoder.getAddressCoordinates(deliveryPostalCode))
    // Store cached answer
    this.cachedLocationSearches.set(deliveryPostalCode, {timestamp: Date.now(), result: result})
    return result
  }
}
